import React from 'react';
import { MapPin, Calendar, Users, CreditCard, TrendingUp, Landmark, Sun } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости поездки
 */
const ResultsOverview = ({ results, formData, getDestinationName }) => {
  if (!results) return null;

  // Определение сезона
  const getSeasonName = (seasonId) => {
    const seasonNames = {
      'low': 'Низкий сезон',
      'shoulder': 'Межсезонье',
      'high': 'Высокий сезон',
      'peak': 'Пиковый сезон'
    };
    return seasonNames[seasonId] || 'Не определен';
  };

  // Функция для форматирования больших чисел с разделителями
  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <ToolResult title="Результаты расчета стоимости поездки">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 p-5 border border-purple-100 dark:border-purple-800/50 shadow-sm">
          <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4"/>
            Общая стоимость поездки
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-purple-800 dark:text-purple-200" aria-live="polite">
              {formatPrice(results.totalCost)}
            </span>
          </div>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            На {results.numTravelers} {pluralize(results.numTravelers, 'человека', 'человек', 'человек')} на {results.tripDuration} {pluralize(results.tripDuration, 'день', 'дня', 'дней')}
          </p>
        </div>

        {/* Стоимость на человека */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Users className="w-4 h-4"/>
            Стоимость на 1 человека
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatPrice(results.costPerPerson)}
            </span>
            <span className="text-base font-medium text-indigo-600 dark:text-indigo-400">
              ({formatPrice(results.costPerPersonPerDay)}/день)
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Включая все выбранные опции и расходы
          </p>
        </div>
      </div>

      {/* Основная информация о поездке */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Направление" 
          value={getDestinationName(results.destination)} 
          color="purple" 
          icon={<MapPin className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Продолжительность" 
          value={`${results.tripDuration} ${pluralize(results.tripDuration, 'день', 'дня', 'дней')}`} 
          color="blue" 
          icon={<Calendar className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Количество путешественников" 
          value={`${results.numTravelers} ${pluralize(results.numTravelers, 'человек', 'человека', 'человек')}`} 
          icon={<Users className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Туристический сезон" 
          value={getSeasonName(results.season)} 
          color="orange"
          icon={<Sun className="w-4 h-4"/>} 
        />

        {/* Разбивка стоимости по категориям */}
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <Landmark className="w-4 h-4 mr-2 text-purple-500"/>
            Разбивка расходов
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-purple-500 mr-2"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Транспорт</span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(results.transportCost)}</span>
                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({results.expensesStructure.transport}%)</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Проживание</span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(results.accommodationCost)}</span>
                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({results.expensesStructure.accommodation}%)</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Питание</span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(results.foodCost)}</span>
                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({results.expensesStructure.food}%)</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-indigo-500 mr-2"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Развлечения</span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(results.entertainmentCost)}</span>
                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({results.expensesStructure.entertainment}%)</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Шоппинг</span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(results.shoppingCost)}</span>
                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({results.expensesStructure.shopping}%)</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Прочее</span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(results.otherCosts)}</span>
                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({results.expensesStructure.other}%)</span>
              </div>
            </div>
          </div>

          {/* Визуализация разбивки бюджета */}
          <div className="mt-4 h-6 flex rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
            <div className="h-full bg-purple-500" style={{ width: `${results.expensesStructure.transport}%` }} title="Транспорт"></div>
            <div className="h-full bg-blue-500" style={{ width: `${results.expensesStructure.accommodation}%` }} title="Проживание"></div>
            <div className="h-full bg-yellow-500" style={{ width: `${results.expensesStructure.food}%` }} title="Питание"></div>
            <div className="h-full bg-indigo-500" style={{ width: `${results.expensesStructure.entertainment}%` }} title="Развлечения"></div>
            <div className="h-full bg-green-500" style={{ width: `${results.expensesStructure.shopping}%` }} title="Шоппинг"></div>
            <div className="h-full bg-orange-500" style={{ width: `${results.expensesStructure.other}%` }} title="Прочее"></div>
          </div>
          <div className="flex justify-center mt-2 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center mr-2">
              <div className="w-2 h-2 rounded-full bg-purple-500 mr-1"></div>
              <span>Транспорт</span>
            </div>
            <div className="flex items-center mr-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 mr-1"></div>
              <span>Жильё</span>
            </div>
            <div className="flex items-center mr-2">
              <div className="w-2 h-2 rounded-full bg-yellow-500 mr-1"></div>
              <span>Еда</span>
            </div>
            <div className="flex items-center mr-2">
              <div className="w-2 h-2 rounded-full bg-indigo-500 mr-1"></div>
              <span>Развлечения</span>
            </div>
            <div className="flex items-center mr-2">
              <div className="w-2 h-2 rounded-full bg-green-500 mr-1"></div>
              <span>Шоппинг</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 rounded-full bg-orange-500 mr-1"></div>
              <span>Прочее</span>
            </div>
          </div>
        </div>
      </div>

      {/* Экономия и рекомендации */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Экономия и рекомендации</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {results.costPerPersonPerDay < 2500 
                ? 'Вы выбрали бюджетный вариант поездки. Это хороший баланс между стоимостью и комфортом.' 
                : results.costPerPersonPerDay < 5000 
                  ? 'Вы выбрали средний по стоимости вариант поездки. Хороший баланс между комфортом и расходами.' 
                  : 'Вы выбрали комфортный вариант путешествия с широкими возможностями для отдыха и развлечений.'
              }
              {results.season === 'low' && ' Путешествие в низкий сезон позволяет сэкономить до 30% на размещении и развлечениях.'}
              {results.season === 'peak' && ' Путешествие в пиковый сезон обычно на 30-50% дороже, но предлагает лучшие погодные условия и максимальную доступность активностей.'}
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (count, one, few, many) => {
  const absCount = Math.abs(count);
  const lastDigit = absCount % 10;
  const lastTwoDigits = absCount % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return many;
  if (lastDigit === 1) return one;
  if (lastDigit >= 2 && lastDigit <= 4) return few;
  return many;
};

export default ResultsOverview;